import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import AddEditBranch from './AddEditBranch'
import ViewBranch from './ViewBranch'
import ChangeAdminModal from './ChangeAdminModal'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import SearchWithFilter from '../common/SearchWithFilter'
import EmptyPageResults from '../common/EmptyPageResults'
import LoadingSpinner from '../common/LoadingSpinner'
import Dropdown from '../common/Dropdown'
import SimpleButton from '../common/SimpleButton'
import GenericTable from '../common/GenericTable'
import ActionsMenu from '../common/ActionsMenu'
import ConfirmationModal from '../common/ConfirmationModal'
import { toast } from '../common/Toast'
import countries from '../../utils/countries.json'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon } from '../../icons'
import { loadBranches, removeBranch, clearBranchesMessages, loadBranchesCountryOptions } from '../../actions/branches'
import { branchesDeletePermission, branchesWritePermission } from '../../utils/permissionValidation'
import { useWindowSize } from '../../libs/hooks'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'

const orderingFields = [
  {
    field: 'branch',
    order: 'asc',
    translationKey: 'branch_asc',
  },
  {
    field: 'branch',
    order: 'desc',
    translationKey: 'branch_desc',
  },
  {
    field: 'organizations',
    order: 'asc',
    translationKey: 'organization_asc',
  },
  {
    field: 'organizations',
    order: 'desc',
    translationKey: 'organization_desc',
  },
  {
    field: 'members',
    order: 'asc',
    translationKey: 'members_asc',
  },
  {
    field: 'members',
    order: 'desc',
    translationKey: 'members_desc',
  },
]

const Branches = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [branchesTableData, setBranchesTableData] = useState([])
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [countriesFilter, setCountriesFilter] = useState([])
  const [accountTypeFilter, setAccountTypeFilter] = useState([])
  const [organizationTypeFilter, setOrganizationTypeFilter] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [showAddEditBranchModal, setShowAddEditBranchModal] = useState(false)
  const [showViewBranchModal, setShowViewBranchModal] = useState(false)
  const [showDeleteBranchConfirmModal, setShowDeleteBranchConfirmModal] = useState(false)
  const [showChangeCompanyAdminModal, setShowChangeCompanyAdminModal] = useState(false)
  const [branchIdForAction, setBranchIdForAction] = useState(null)
  const [companyIdForAction, setCompanyIdForAction] = useState(null)
  const [expandedBranch, setExpandedBranch] = useState(0)
  const [selectedBranch, setSelectedBranch] = useState(null)
  const organizationTypeOptions = ['organization_context.single_branch', 'corporate_dso']
  const accountTypeOptions = ['End User', 'Dealer']

  useEffect(() => {
    if (props.countryOptions.length === 0) {
      props.actions.loadBranchesCountryOptions()
    }
  }, [props.actions])

  useEffect(() => {
    const tableData = props.branches.map((branch, index) => {
      return {
        index,
        identifier: branch.id,
        organization: branch.company?.name,
        branch: branch.branchName,
        country: branch.country,
        admin: branch.email,
        isCorporateBranch: branch.company?.isCorporation,
        members: branch.members,
        devices: branch.totalDevices,
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditBranch(branch.id)
            },
            requiredPermission: branchesWritePermission,
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditBranch(branch.id, true)
            },
            requiredPermission: branchesWritePermission,
          },
          // {
          //   label: 'delete',
          //   onActionClick: (e) => {
          //     e.stopPropagation()
          //     showDeleteConfirmationModal(branch.id, branch.company?.id)
          //   },
          //   requiredPermission: branchesDeletePermission,
          // },
          ...(branch.company?.isCorporation
            ? [
                {
                  label: 'organization_context.change_branch_admin',
                  onActionClick: (e) => {
                    e.stopPropagation()
                    handleOpenChangeBranchAdmin(branch.id)
                  },
                  requiredPermission: branchesWritePermission,
                },
              ]
            : []),
        ],
      }
    })

    setBranchesTableData(tableData)
  }, [props.branches])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearBranchesMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearBranchesMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadBranches(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadBranches(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, countriesFilter, organizationTypeFilter, accountTypeFilter, orderBy])

  const handleLoadBranches = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      offset: withReset ? 0 : (selectedPage - 1) * process.env.REACT_APP_PAGINATION_SIZE,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
    }

    props.actions.loadBranches(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setCountriesFilter([])
    setOrganizationTypeFilter([])
    setAccountTypeFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (countriesFilter.length > 0) {
      queryParams = { ...queryParams, country: countriesFilter }
    }

    if (organizationTypeFilter.length > 0) {
      queryParams = { ...queryParams, isCorporation: organizationTypeFilter.includes('corporate_dso') ? true : false }
    }

    if (accountTypeFilter.length > 0) {
      queryParams = { ...queryParams, account: accountTypeFilter }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleOpenViewEditBranch = (branchId, isEditModal) => {
    if (!branchId) {
      return
    }

    const branch = props.branches.find((branch) => branch.id === branchId)

    if (branch) {
      setSelectedBranch(branch)
      isEditModal ? setShowAddEditBranchModal(true) : setShowViewBranchModal(true)
    }
  }

  const showDeleteConfirmationModal = (branchId, companyId) => {
    setBranchIdForAction(branchId)
    setCompanyIdForAction(companyId)
    setShowDeleteBranchConfirmModal(true)
  }

  const handleDeleteBranch = () => {
    setShowDeleteBranchConfirmModal(false)
    props.actions.removeBranch(companyIdForAction, branchIdForAction)
  }

  const handleOpenChangeBranchAdmin = (branchId) => {
    const branch = props.branches.find((branch) => branch.id === branchId)

    if (branch) {
      setShowChangeCompanyAdminModal(true)
      setSelectedBranch(branch)
    }
  }

  const closeModal = () => {
    setShowAddEditBranchModal(false)
    setShowViewBranchModal(false)
    setShowChangeCompanyAdminModal(false)
    setSelectedBranch(null)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  return (
    <Fragment>
      <div className="branches-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={countriesFilter.length > 0 || organizationTypeFilter.length > 0 || accountTypeFilter.length > 0}
            counterBadge={countriesFilter.length + organizationTypeFilter.length + accountTypeFilter.length}
          />
          {/*A.S:Currently only allowing adding branch to specific organizations */}
          {/* <SimpleButton className="action-button" onClick={() => setShowAddEditBranchModal(true)}>
            {t('organization_context.add_branch')}
          </SimpleButton> */}
        </div>
        {isMobile && branchesTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && branchesTableData.length > 0 && (
          <GenericTable
            data={branchesTableData}
            headers={[
              {
                title: t('organization'),
                orderKey: 'organizations',
              },
              {
                title: t('branch'),
                orderKey: 'branch',
              },
              {
                title: t('country'),
              },
              {
                title: t('admin'),
              },
              {
                title: t('organization_context.corporate_branch'),
              },
              {
                title: t('nav.members'),
                orderKey: 'members',
              },
              {
                title: t('devices'),
              },
              {
                title: '',
              },
            ]}
            keys={['organization', 'branch', 'country', 'admin', 'isCorporateBranch', 'members', 'devices', 'action']}
            keyRenderer={{
              country: (item) => countries.find((country) => country.code === item.country)?.name,
              isCorporateBranch: (item) => (item.isCorporateBranch ? t('yes') : t('no')),
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(branch) => handleOpenViewEditBranch(branch.identifier)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          branchesTableData.map((branch, index) => (
            <div key={index} className="card" onClick={() => setExpandedBranch(index)}>
              <div className={'card-item' + (expandedBranch !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('organization')}</div>
                <div className={'card-item-body' + (expandedBranch !== index ? ' align-center' : '')}>
                  <div>{branch.organization}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={branch.actions} isLoading={showSpinner} />}
                  </div>
                </div>
              </div>
              {expandedBranch === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('branch')}</div>
                    <div className="card-item-body">{branch.branch}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('country')}</div>
                    <div className="card-item-body">{countries.find((country) => country.code === branch.country)?.name}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('admin')}</div>
                    <div className="card-item-body">{branch.admin}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('organization_context.corporate_branch')}</div>
                    <div className="card-item-body">{branch.isCorporateBranch ? t('yes') : t('no')}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('nav.members')}</div>
                    <div className="card-item-body">{branch.members}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('devices')}</div>
                    <div className="card-item-body">{branch.devices}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && branchesTableData.length === 0 && (
          <EmptyPageResults title={t('organization_context.no-branches-found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: props.countryOptions,
              input: countriesFilter,
              output: (filterCountries) => {
                setCountriesFilter(filterCountries)
              },
            },
            {
              name: t('organization_context.organization_type'),
              type: FilterType.multiSelect,
              dataset: organizationTypeOptions,
              input: organizationTypeFilter,
              output: (filterOrganizationTypes) => {
                setOrganizationTypeFilter(filterOrganizationTypes)
              },
            },
            {
              name: t('account_context.account_type'),
              type: FilterType.multiSelect,
              dataset: accountTypeOptions,
              input: accountTypeFilter,
              output: (filterAccountTypes) => {
                setAccountTypeFilter(filterAccountTypes)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showAddEditBranchModal && (
        <AddEditBranch selectedBranch={selectedBranch} closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />
      )}
      {showViewBranchModal && (
        <ViewBranch
          selectedBranch={selectedBranch}
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
          handleEditBranch={() => {
            setShowViewBranchModal(false)
            setShowAddEditBranchModal(true)
          }}
          closeModal={closeModal}
        />
      )}
      {showChangeCompanyAdminModal && (
        <ChangeAdminModal selectedBranch={selectedBranch} closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />
      )}
      {showDeleteBranchConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteBranchConfirmModal(false)}
          onAccept={() => handleDeleteBranch()}
          onCancel={() => setShowDeleteBranchConfirmModal(false)}
          message={t('organization_context.confirm_delete_branch')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={branchesDeletePermission}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ branches }) {
  return {
    branches: branches?.branches || [],
    countryOptions: branches?.countryOptions?.map((country) => country.countryCode) || [],
    isLoading: branches?.isLoading,
    canLoadMore: branches?.canLoadMore,
    errorMessage: branches?.errorMessage,
    successMessage: branches?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadBranches,
        loadBranchesCountryOptions,
        removeBranch,
        clearBranchesMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Branches)

